// Files waiting in the chat composer. Each one is prepared (a big photo is scaled down to a JPEG first, the
// rest goes as it is), then uploaded to the thread straight away, one at a time in the order they were
// added, so Send only has to name them. Chips show a thumbnail, the progress and any error; removing one
// stops its upload, or takes the uploaded file back. What's waiting stays with its thread while the
// panel is closed or another conversation is open (for this visit to the page); a reload or a closed tab
// takes it back from the server too.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CHAT_IMAGE_MAX_EDGE,
  CHAT_IMAGE_QUALITY,
  CHAT_IMAGE_REENCODE_BYTES,
  CHAT_MAX_FILE_BYTES,
  CHAT_MAX_FILES,
  CHAT_MAX_PENDING_UPLOADS,
  chatFileKind,
  formatFileSize,
  type ChatAttachment,
  type ChatFileKind,
} from '@technohull/shared';
import { decodeImage, encodeJpeg, fitWithin, imageTypeOf, jpegName, readAsDataUrl, type DecodedImage, type ShrinkDeps } from '../lib/shrinkImage.js';
import { discardChatUpload, discardChatUploadsOnLeave, uploadChatFile } from './collabHooks.js';

/** a file the app won't send, with a message fit to show. */
export class ChatFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ChatFileError';
  }
}

export interface PreparedFile { blob: Blob; name: string; type: string; width: number | null; height: number | null; preview: string | null }

// photos the browser can make smaller; a GIF keeps its animation and goes as it is
const SHRINKABLE = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/avif']);
const THUMB_EDGE = 160;
const tooBig = (name: string, size: number) =>
  new ChatFileError(`“${name}” is ${formatFileSize(size)} - files can be up to ${formatFileSize(CHAT_MAX_FILE_BYTES)}.`);

/** a file ready to upload. A photo over CHAT_IMAGE_MAX_EDGE (or over CHAT_IMAGE_REENCODE_BYTES, or one most
 *  browsers can't show, like HEIC) becomes a JPEG at that edge; a small photo, a GIF and any other file go as
 *  they are. Images get a small preview for the chip and their size, so the bubble keeps its shape. */
export async function prepareChatFile(file: File, deps: ShrinkDeps = {}): Promise<PreparedFile> {
  const name = file.name || 'file';
  if (!file.size) throw new ChatFileError(`“${name}” is empty.`);
  const type = imageTypeOf(file);
  const isImage = type.startsWith('image/') && !type.includes('svg');
  if (!isImage) {
    if (file.size > CHAT_MAX_FILE_BYTES) throw tooBig(name, file.size);
    return { blob: file, name, type, width: null, height: null, preview: null };
  }
  const encode = deps.encode ?? encodeJpeg;
  let img: DecodedImage;
  try {
    img = await (deps.decode ?? decodeImage)(file);
  } catch {
    // this browser can't read it (HEIC on a desktop): it goes as it is, as a file
    if (file.size > CHAT_MAX_FILE_BYTES) throw tooBig(name, file.size);
    return { blob: file, name, type, width: null, height: null, preview: null };
  }
  try {
    const thumbFit = fitWithin(img.width, img.height, THUMB_EDGE);
    const thumb = thumbFit.width ? await encode(img, thumbFit.width, thumbFit.height, 0.7).catch(() => null) : null;
    const preview = thumb?.size ? await readAsDataUrl(thumb).catch(() => null) : null;
    const fit = fitWithin(img.width, img.height, CHAT_IMAGE_MAX_EDGE);
    const viewable = chatFileKind(type) === 'image';
    const reencode = SHRINKABLE.has(type) && fit.width > 0 && (fit.scaled || file.size > CHAT_IMAGE_REENCODE_BYTES || !viewable);
    if (reencode) {
      const out = await encode(img, fit.width, fit.height, CHAT_IMAGE_QUALITY).catch(() => null);
      // keep the JPEG when it had to be scaled or converted, or when it really is smaller
      if (out?.size && (fit.scaled || !viewable || out.size < file.size * 0.9)) {
        if (out.size > CHAT_MAX_FILE_BYTES) throw tooBig(name, out.size);
        return { blob: out, name: jpegName(name), type: 'image/jpeg', width: fit.width, height: fit.height, preview };
      }
    }
    if (file.size > CHAT_MAX_FILE_BYTES) throw tooBig(name, file.size);
    return { blob: file, name, type, width: viewable ? img.width : null, height: viewable ? img.height : null, preview: viewable ? preview : null };
  } finally {
    img.close?.();
  }
}

export interface PendingUpload {
  key: number;
  file: File;
  name: string;
  size: number;
  kind: ChatFileKind;
  /** a small data: thumbnail (the app's CSP allows data: images, not blob:) */
  preview: string | null;
  status: 'queued' | 'preparing' | 'uploading' | 'done' | 'error';
  /** 0..1 while uploading */
  progress: number;
  error: string | null;
  attachment: ChatAttachment | null;
}

export interface ChatUploads {
  items: PendingUpload[];
  /** the uploaded files' ids, in the order they were added */
  readyIds: string[];
  /** something is still being prepared or uploaded */
  busy: boolean;
  /** an upload failed (retry or remove it before sending) */
  failed: boolean;
  /** a file that wasn't added and why (too many, too big) */
  notice: string | null;
  clearNotice: () => void;
  add: (files: File[]) => void;
  remove: (key: number) => void;
  retry: (key: number) => void;
  /** after sending: the files sent belong to the message now (`sent`: their ids; a file added while it was
   *  sending stays for the next message). With no ids, every file goes. */
  reset: (sent?: readonly string[]) => void;
}

// what's waiting per thread while its view is closed (finished uploads, and the ones still to do)...
const waiting = new Map<string, PendingUpload[]>();
// ...and in the views open now
const shown = new Map<string, () => PendingUpload[]>();
let seq = 0;

/** the files this page has uploaded that are still waiting to be sent, in every thread. */
export function waitingUploadIds(): string[] {
  const all = [...waiting.values(), ...[...shown.values()].map((get) => get())].flat();
  return all.flatMap((x) => (x.status === 'done' && x.attachment ? [x.attachment.id] : []));
}

// a reload or a closed tab takes this page's waiting files back with it: they vanish from the composer, and
// the server would otherwise keep them for a day, counting against the sender's cap. A page put aside for
// the back button (persisted) keeps them, as it comes back as it was.
let watchingLeave = false;
function discardOnLeave() {
  if (watchingLeave || typeof window === 'undefined') return;
  watchingLeave = true;
  window.addEventListener('pagehide', (e) => {
    if ((e as PageTransitionEvent).persisted) return;
    const ids = waitingUploadIds();
    if (ids.length) discardChatUploadsOnLeave(ids.slice(0, CHAT_MAX_PENDING_UPLOADS));
  });
}

export function useChatUploads(thread: string, deps?: { prepare?: typeof prepareChatFile; upload?: typeof uploadChatFile }): ChatUploads {
  const [items, setItems] = useState<PendingUpload[]>(() => waiting.get(thread) ?? []);
  const [notice, setNotice] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const running = useRef<number | null>(null);
  const aborts = useRef(new Map<number, AbortController>());
  const prepare = deps?.prepare ?? prepareChatFile;
  const upload = deps?.upload ?? uploadChatFile;

  const patch = useCallback((key: number, p: Partial<PendingUpload>) => {
    setItems((list) => list.map((x) => (x.key === key ? { ...x, ...p } : x)));
  }, []);

  // one at a time, in the order they were added, so a message keeps its files in that order
  useEffect(() => {
    if (running.current != null) return;
    const next = items.find((x) => x.status === 'queued');
    if (!next) return;
    running.current = next.key;
    const ac = new AbortController();
    aborts.current.set(next.key, ac);
    // stopped part-way: a removed chip is gone already; one still here goes back in the queue
    const stopped = () => patch(next.key, { status: 'queued', progress: 0 });
    void (async () => {
      try {
        patch(next.key, { status: 'preparing', progress: 0, error: null });
        const ready = await prepare(next.file);
        if (ac.signal.aborted) return stopped();
        patch(next.key, { status: 'uploading', name: ready.name, size: ready.blob.size, kind: chatFileKind(ready.type), preview: ready.preview ?? next.preview });
        const { attachment } = await upload(thread, ready, { signal: ac.signal, onProgress: (f) => patch(next.key, { progress: f }) });
        if (ac.signal.aborted) { void discardChatUpload(attachment.id); return stopped(); }
        patch(next.key, { status: 'done', progress: 1, attachment });
      } catch (e) {
        if (ac.signal.aborted) stopped();
        else patch(next.key, { status: 'error', error: e instanceof Error ? e.message : 'The upload failed.' });
      } finally {
        aborts.current.delete(next.key);
        running.current = null;
        setTick((t) => t + 1);
      }
    })();
  }, [items, tick, thread, patch, prepare, upload]);

  useEffect(() => {
    discardOnLeave();
    shown.set(thread, () => itemsRef.current);
    return () => { shown.delete(thread); };
  }, [thread]);

  // leaving the thread: stop what's uploading (it starts again on the way back) and keep the rest waiting
  useEffect(() => () => {
    for (const ac of aborts.current.values()) ac.abort();
    const keep = itemsRef.current
      .filter((x) => x.status !== 'error')
      .map((x) => (x.status === 'done' ? x : { ...x, status: 'queued' as const, progress: 0 }));
    if (keep.length) waiting.set(thread, keep);
    else waiting.delete(thread);
  }, [thread]);

  const add = useCallback((files: File[]) => {
    setNotice(null);
    const room = Math.max(0, CHAT_MAX_FILES - itemsRef.current.length);
    const notes: string[] = [];
    if (files.length > room) notes.push(`A message can carry up to ${CHAT_MAX_FILES} files.`);
    const fresh: PendingUpload[] = [];
    for (const f of files.slice(0, room)) {
      const type = imageTypeOf(f);
      // only a photo can come out smaller, so anything else over the limit is turned away now
      if (!SHRINKABLE.has(type) && f.size > CHAT_MAX_FILE_BYTES) { notes.push(tooBig(f.name || 'file', f.size).message); continue; }
      fresh.push({
        key: ++seq, file: f, name: f.name || 'file', size: f.size, kind: chatFileKind(type), preview: null,
        status: 'queued', progress: 0, error: null, attachment: null,
      });
    }
    if (notes.length) setNotice(notes.join(' '));
    if (fresh.length) setItems((list) => [...list, ...fresh]);
  }, []);

  const remove = useCallback((key: number) => {
    const it = itemsRef.current.find((x) => x.key === key);
    aborts.current.get(key)?.abort();
    if (it?.attachment) void discardChatUpload(it.attachment.id);
    setItems((list) => list.filter((x) => x.key !== key));
  }, []);

  const retry = useCallback((key: number) => patch(key, { status: 'queued', error: null, progress: 0 }), [patch]);
  const reset = useCallback((sent?: readonly string[]) => {
    setItems((list) => (sent ? list.filter((x) => !(x.attachment && sent.includes(x.attachment.id))) : []));
    setNotice(null);
    waiting.delete(thread);
  }, [thread]);
  const clearNotice = useCallback(() => setNotice(null), []);

  return useMemo(() => ({
    items,
    readyIds: items.flatMap((x) => (x.status === 'done' && x.attachment ? [x.attachment.id] : [])),
    busy: items.some((x) => x.status === 'queued' || x.status === 'preparing' || x.status === 'uploading'),
    failed: items.some((x) => x.status === 'error'),
    notice,
    clearNotice,
    add,
    remove,
    retry,
    reset,
  }), [items, notice, clearNotice, add, remove, retry, reset]);
}

/** the files on a clipboard or in a drop (an image copied from a page or a screenshot arrives as a file). */
export function filesOf(data: DataTransfer | null): File[] {
  if (!data) return [];
  const list = Array.from(data.files ?? []);
  if (list.length) return list;
  return Array.from(data.items ?? []).flatMap((it) => {
    if (it.kind !== 'file') return [];
    const f = it.getAsFile();
    return f ? [f] : [];
  });
}
